import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, School as SchoolIcon, Building, Building2, MapPin, Package } from 'lucide-react';
import ReactDOM from 'react-dom';
import { globalSearch } from '../api';

/**
 * Supply-Ops Global Search — searches schools, suppliers, items, cities,
 * and education administrations against the single supply data source.
 */
export default function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, width: 380 });

  useEffect(() => {
    const nq = q.trim();
    if (nq.length < 2) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      globalSearch(nq, 6)
        .then((r) => { if (!cancelled) setData(r); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 240);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setStyle({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 400) });
  }, [open, q]);

  useEffect(() => {
    function onDoc(e) {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (path) => { setOpen(false); setQ(''); nav(path); };

  const totalHits = useMemo(() => {
    if (!data?.counts) return 0;
    return Object.values(data.counts).reduce((a, b) => a + (b || 0), 0);
  }, [data]);

  return (
    <div ref={anchorRef} className="panel px-3 py-2 flex items-center gap-2 min-w-[280px] max-w-[420px]" data-testid="global-search-root">
      <Search size={14} className="text-slate-400 shrink-0" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') { setOpen(false); setQ(''); } }}
        placeholder="ابحث: مدرسة، مورد، إدارة تعليمية، صنف..."
        className="bg-transparent outline-none text-slate-100 text-[12.5px] flex-1 min-w-0 text-right placeholder:text-slate-500"
      />
      {loading && <Loader2 size={13} className="animate-spin text-slate-500" />}

      {open && q.trim().length >= 2 && ReactDOM.createPortal(
        <div ref={popRef} dir="rtl"
             className="bg-[#0f0e19] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
             style={{ position: 'fixed', zIndex: 10000, ...style }}
             data-testid="global-search-panel">
          {(!data || totalHits === 0) && !loading && (
            <div className="px-4 py-3 text-slate-500 text-[12px]">لا توجد نتائج</div>
          )}
          {data && (
            <div className="max-h-[520px] overflow-y-auto divide-y divide-white/5">
              <Section title="المدارس" icon={SchoolIcon} rows={data.results.schools} render={(s, i) => (
                <button key={`sc-${s.school_internal_id || i}`}
                        className="row-btn" data-testid="search-result-school"
                        onClick={() => go(`/schools?q=${encodeURIComponent(s.school_name || s.school_id || '')}`)}>
                  <div className="truncate font-semibold text-slate-100">{s.school_name || '—'}</div>
                  <div className="text-[10.5px] text-slate-400 truncate">
                    {s.school_id || <span style={{ color: '#F59E0B' }}>بدون رقم وزاري</span>}
                    {' · '}{s.governorate || s.region || '—'}
                    {' · '}{Number(s.allocated_qty).toLocaleString('en-US')} قطعة
                  </div>
                </button>
              )} />
              <Section title="الموردون" icon={Building} rows={data.results.suppliers} render={(s, i) => (
                <button key={`sp-${i}`} className="row-btn" data-testid="search-result-supplier"
                        onClick={() => go(`/suppliers/${encodeURIComponent(s.name)}`)}>
                  <div className="truncate font-semibold text-slate-100">{s.name}</div>
                  <div className="text-[10.5px] text-slate-400">
                    {(s.schools || 0).toLocaleString('en-US')} مدرسة · {Number(s.allocated_value).toLocaleString('en-US', { maximumFractionDigits: 0 })} ر.س
                  </div>
                </button>
              )} />
              <Section title="الإدارات التعليمية" icon={Building2} rows={data.results.administrations} render={(a, i) => (
                <button key={`ad-${i}`} className="row-btn" data-testid="search-result-admin"
                        onClick={() => go(`/administrations/${encodeURIComponent(a.name)}`)}>
                  <div className="truncate font-semibold text-slate-100">{a.name}</div>
                  <div className="text-[10.5px] text-slate-400">
                    {(a.schools || 0).toLocaleString('en-US')} مدرسة · {Number(a.allocated_value).toLocaleString('en-US', { maximumFractionDigits: 0 })} ر.س
                  </div>
                </button>
              )} />
              <Section title="المدن" icon={MapPin} rows={data.results.cities} render={(c, i) => (
                <button key={`ct-${i}`} className="row-btn" data-testid="search-result-city"
                        onClick={() => go(`/schools?q=${encodeURIComponent(c.name)}`)}>
                  <div className="truncate font-semibold text-slate-100">{c.name}</div>
                  <div className="text-[10.5px] text-slate-400">{(c.schools || 0).toLocaleString('en-US')} مدرسة</div>
                </button>
              )} />
              <Section title="الأصناف" icon={Package} rows={data.results.items} render={(it, i) => (
                <button key={`it-${i}`} className="row-btn" data-testid="search-result-item"
                        onClick={() => go(`/items-list?q=${encodeURIComponent(it.name)}`)}>
                  <div className="truncate font-semibold text-slate-100">{it.name}</div>
                  <div className="text-[10.5px] text-slate-400">{Number(it.allocated_qty).toLocaleString('en-US')} قطعة</div>
                </button>
              )} />
            </div>
          )}
          <style>{`
            .row-btn { display: block; width: 100%; text-align: right; padding: 7px 14px; transition: background .15s; }
            .row-btn:hover { background: rgba(109,93,251,0.15); }
          `}</style>
        </div>,
        document.body,
      )}
    </div>
  );
}

function Section({ title, icon: Icon, rows, render }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="py-1">
      <div className="flex items-center gap-2 px-3 py-2 text-[10px] tracking-widest font-semibold text-slate-500 uppercase">
        <Icon size={11} /> {title}
      </div>
      {rows.map(render)}
    </div>
  );
}
